import * as React from 'react';

import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { BrowserTitleService } from '../../../ts/services/BrowserTitleService';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { updateBreadcrumbs } from '../../actions/UpdateBreadcrumbsAction';
import { appStore } from '../../AppStore';
import { BreadcrumbStateInfo } from '../../content/breadcrumbs/BreadcrumbStateInfo';
import { ServerUrls } from '../../ServerUrls';
import { ServiceLocator } from '../../ServiceLocator';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '../../state/SpaceInfo';
import { ConnectedQueryParamUpdateProvider } from '../RouterHelpers/QueryParamUpdateProvider';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { AllSchedulesSpaceAngularProps, AllSchedulesSpaceOwnProps } from './AllSchedulesSpace';
import { ConnectedAllSchedulesSpace } from './ConnectedAllSchedulesSpace';

export interface AllSchedulesSpaceProviderProps extends ReactDirectiveProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls | ServerUrls;
}

const AllSchedulesSpaceAngularHOC = angularProviderHOC<AllSchedulesSpaceOwnProps, AllSchedulesSpaceAngularProps, AllSchedulesSpaceAngularProps>({
  getAngularServicesWithServiceLocator: () => ({
    browserTitleService: ServiceLocator.get<BrowserTitleService>('BrowserTitleService'),
    telemetry: ServiceLocator.get<Telemetry>('TelemetryService'),
  }),
  selectPropsFromAngularServices: angularServices => angularServices,
});

const AngularWrappedAllSchedulesSpace: React.ComponentType<AllSchedulesSpaceOwnProps>
  = AllSchedulesSpaceAngularHOC(ConnectedAllSchedulesSpace);

const AllSchedulesSpaceInfo: SpaceInfo = {
  item: SpaceItemForNonEntitySpace,
  spaceType: 'schedules',
  subspaceName: undefined
};

export const AllSchedulesSpaceProvider: React.FunctionComponent<AllSchedulesSpaceProviderProps> = (props) => {

  React.useEffect(() => {
    appStore.dispatch(updateBreadcrumbs([getSchedulesPlaceBreadcrumb(props.urls)]));
  }, []);

  return (
    <ConnectedSpaceInfoProvider spaceInfo={AllSchedulesSpaceInfo}>
      <ConnectedQueryParamUpdateProvider
        routeLocationPath={props.routeLocationPath}
        routeLocationSearch={props.routeLocationSearch}
        shouldUpdateBreadcrumbs={true}
        shouldUpdateContentTypes={false}
        shouldUpdateFilters={false}
        shouldUpdateSort={true}
      >
        <AngularWrappedAllSchedulesSpace angularContext={props.angularContext}/>
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
};

export function getSchedulesPlaceBreadcrumb(urls: SpacesSiteUrls | ServerUrls): BreadcrumbStateInfo {
  const href = urls.schedulesUrl();
  return {
    id: href,
    text: Messages.allSchedules(),
    href: href,
    testId: 'schedules-link'
  };
}
